import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import { z } from "zod";

export type NotificationTopicJSON = z.infer<typeof NotificationTopicSchema.NotificationTopicJSONZ>;
export type NotificationTopicParams = z.infer<typeof NotificationTopicSchema.NotificationTopicZ>;

export default class NotificationTopicSchema {
  public static readonly NotificationTopicJSONZ = z.object({
    topicCategory: z.string().nullish(),
    topicId: z.uuid().nullish(),
  });

  public static readonly NotificationTopicZ = z.object({
    category: z.string().optional(),
    id: z.instanceof(Guid).nullish(),
  });

  public static validateParams(values: unknown): NotificationTopicParams {
    return parseOrThrow(NotificationTopicSchema.NotificationTopicZ, values, {
      logError: true,
      schemaName: "NotificationTopicParams",
      operation: "validateParams",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): NotificationTopicJSON {
    return parseOrThrow(NotificationTopicSchema.NotificationTopicJSONZ, values, {
      logError: true,
      schemaName: "NotificationTopicJSON",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static areValidParams(values: unknown): values is NotificationTopicParams {
    try {
      NotificationTopicSchema.validateParams(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is NotificationTopicJSON {
    try {
      NotificationTopicSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: unknown): NotificationTopicParams {
    const parsed = parseOrThrow(NotificationTopicSchema.NotificationTopicJSONZ, data, {
      logError: true,
      schemaName: "NotificationTopicJSON",
      operation: "deserialize",
      payloadSample: data,
    });
    return NotificationTopicSchema.codec.decode(parsed);
  }

  public static serialize(topic: NotificationTopicParams): NotificationTopicJSON {
    const parsed = NotificationTopicSchema.validateParams(topic);
    return NotificationTopicSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    NotificationTopicSchema.NotificationTopicJSONZ,
    NotificationTopicSchema.NotificationTopicZ,
    {
      decode: (data) => ({
        category: data.topicCategory ?? undefined,
        id: Guid.fromJSON(data.topicId) ?? null,
      }),
      encode: (topic) => ({
        topicCategory: topic.category,
        topicId: topic.id?.toJSON(),
      }),
    }
  );
}
